import React, {useEffect, useReducer, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "./components/SolvProvider"
import {useIntlEx} from "./components/IntlUtils"
import {
  ActionButton,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormGroup,
  FormGroups,
  FormHeader,
  MainContainer,
  useAlert
} from "./components/FormComps.js"
import MainMenu from "./MainMenu"
import {AccountTabs, mkHref, TenantLink} from "./components/AccountUtils";
import {UserAndTimeLink} from "./components/UserUtils";
import {openDialogCurried} from "./components/DialogUtils";
import CreditTransactionReverseDialog from "./CreditTransactionReverseDialog";
import CreditTransactionBill from "./CreditTransactionBill";
import {useReactToPrint} from "react-to-print";
import {shortIdEqual} from "./components/StringUtils";
import CreditTransactionDeleteDialog from "./CreditTransactionDeleteDialog";
import {simpleReducer} from "./components/ReactUtils";
import CreditTransactionSettleDialog from "./CreditTransactionSettleDialog";
import {CreditTransactionStatus} from "./components/CreditTransactionUtils";
import getLogger from "./components/Logging.js"
import './App.css'

const log = getLogger("CreditTransaction")

export default function CreditTransaction(props) {

  const {api, env, session, setFatal, setBusy} = useSolv()
  const {intl } = useIntlEx()

  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [alert, setAlert] = useAlert()
  const [touched, setTouched] = useState(false)

  const [creditTransaction, setCreditTransaction] = useState(null)
  const [billingInfo, updateBillingInfo] = useReducer(simpleReducer, {})
  const [bankAccounts, setBankAccounts] = useState(null)

  const invoiceRef = useRef(null)
  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: creditTransaction?.ref?.invoiceNo,
    pageStyle: `@page { @top-left { content: '  '; } @bottom-left { content: '${window.location.href}'} @bottom-right { content: counter(page) '/' counter(pages); }`
  });

  useEffect(() => {
    if (tenant && params.creditTransactionId) {
      setBusy(intl.msg("working"))
      api.getCreditTransaction(tenant.tenantId, params.creditTransactionId)
        .then(({data}) => {
          log.debug(">>>Z: data=", data)
          setCreditTransaction(data)
          updateBillingInfo(data.tenant?.billingInfo)
          api.getBankAccountsByCurrencyCode(data.tenant?.currencyCode)
            .then(({data}) => {
              setBankAccounts(data)
            })
            .catch((err) => {
              // log.debug("ERROR>>>", err)
              setFatal(err)
            })
        })
        .catch((err) => {
          setFatal(err)
        })
        .finally(() => {
          setBusy(null)
        })
    }
  }, [tenant])

  async function handleEditClick() {
    window.location = mkHref({suffix: `/credits/${params.creditTransactionId}/_edit`, tenant: tenant, user: session.user})
  }

  async function handleSaveClick() {
    setTouched(false)
  }

  async function handleCancelClick() {
    setTouched(false)
  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError: error=", error.code)
      const code = error.code
      if (code === "DUPLICATE_KEY") {
        setAlert({type: "error", text: intl.msg("region_error_duplicate"), field: "inp_regionId", constraint: "unique"})
      }
      else {
        setAlert({type: "error", text: intl.msg("error_failed")})
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function isEditing() {
    return touched
  }

  function isMine() {
    return shortIdEqual(creditTransaction.createdById, session?.user?.userId)
  }

  function FormControls() {

    const ctls = []

    if (creditTransaction) {

      if (isEditing()) {
        ctls.push(
          <ActionButton name="save" flavor="primary" onClick={handleSaveClick}/>
        )
        ctls.push(
          <ActionButton name="cancel" flavor="secondary" onClick={handleCancelClick}/>
        )
      }
      else {
        if (canSettle()) {
          ctls.push(
            <ActionButton name="settle" flavor="primary" onClick={openDialogCurried("dlg_credit_transaction_settle")}/>
          )
        }
        if (canEdit()) {
          ctls.push(
            <ActionButton name="edit" flavor="secondary" onClick={handleEditClick}/>
          )
        }
        ctls.push(
          <MoreMenu/>
        )
      }
    }

    return (ctls)
  }

  function canEdit() {
    return (!isEditing() && creditTransaction && ("PENDING" === creditTransaction.status) && (isMine() || tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*")))
  }

  function canDelete() {
    return (!isEditing() && creditTransaction && ("PENDING" === creditTransaction.status) && (isMine() || tenant.accessingAs("MANAGER/INSTREAM,DOWNSTREAM/*")))
  }

  function canPrint() {
    return (!isEditing() && tenant.accessingAs("REPORTER/INSTREAM,DOWNSTREAM/*") && creditTransaction && (["PURCHASE", "PURCHASE_PENDING"].includes(creditTransaction.creditTransactionType)))
  }

  function canReverse() {
    return (!isEditing() && tenant.accessingAs("ADMINISTRATOR/*/SYSTEM") && creditTransaction && ("ACTIVE" === creditTransaction.status) && (!creditTransaction.reversedOn) && ["PURCHASE", "BONUS", "EXPIRE"].includes(creditTransaction.creditTransactionType))
  }

  function canSettle() {
    return (!isEditing() && tenant.accessingAs("MANAGER/*/SYSTEM") && creditTransaction && ("PENDING" === creditTransaction.status))
  }

  function MoreMenu(props) {

    const cmps = []

    if (canReverse()) {
      cmps.push(
        <DropdownMenuItem msg="reverse" icon="undo" onClick={openDialogCurried("dlg_credit_transaction_reverse")} prefixWithDivider={cmps.length > 0}/>
      )
    }

    if (canPrint()) {
      cmps.push(
        <DropdownMenuItem msg="print" icon="invoice" onClick={handlePrint} prefixWithDivider={cmps.length > 0}/>
      )
    }

    if (canDelete()) {
      cmps.push(
        <DropdownMenuItem name="delete" onClick={openDialogCurried("dlg_credit_transaction_delete")} prefixWithDivider={cmps.length > 0}/>
      )
    }

    return (
      cmps.length > 0 &&
        <DropdownMenu>
          {cmps}
        </DropdownMenu>
    )
  }

  function RelationInfo(props) {
    const ctls = []

    if ("PAYMENT_TRANSACTION" === creditTransaction?.refType) {

      ctls.push(
        <FormGroups>
          <FormGroup disabled={true}>
            <FormGroup.Label text={intl.msg("payment_method")} compact/>
            <FormGroup.Control>
              {intl.msg(`payment_method_${creditTransaction?.paymentMethod?.toLowerCase()}`)}
            </FormGroup.Control>
          </FormGroup>

          {
            creditTransaction?.ref?.invoiceNo &&
              <FormGroup disabled={true}>
                <FormGroup.Label text={intl.msg("credit_transaction_invoice_no")} compact/>
                <FormGroup.Control>
                  {creditTransaction?.ref?.invoiceNo}
                </FormGroup.Control>
              </FormGroup>
          }

          {
            creditTransaction?.ref?.purchaseOrderNo &&
              <FormGroup disabled={true}>
                <FormGroup.Label text={intl.msg("credit_transaction_purchase_order_no")} compact/>
                <FormGroup.Control>
                  {creditTransaction?.ref?.purchaseOrderNo}
                </FormGroup.Control>
              </FormGroup>
          }

          {
            (creditTransaction.details?.payment?.refInfo || creditTransaction?.details?.refInfo) ? (
              <FormGroup disabled={true}>
                <FormGroup.Label text={intl.msg("ref_info")} compact/>
                <FormGroup.Control>
                  {creditTransaction.details?.payment?.refInfo || creditTransaction?.details?.refInfo}
                </FormGroup.Control>
              </FormGroup>
            ) : (creditTransaction.details?.payment?.purchase_units?.[0]?.payments?.captures?.[0]?.id) ? (
              <FormGroup disabled={true}>
                <FormGroup.Label text={intl.msg("credits_paypal_info")} compact/>
                <FormGroup.Control>
                  {creditTransaction.details?.payment?.purchase_units?.[0]?.payments?.captures?.[0]?.id}
                </FormGroup.Control>
              </FormGroup>
            ) : (
              <></>
            )
          }

          {
            creditTransaction?.billingInfo &&
              <>
                <FormGroup disabled={true}>
                  <FormGroup.Label text={intl.msg("credit_transaction_billing_name")} compact/>
                  <FormGroup.Control>
                    {creditTransaction?.billingInfo?.name}
                  </FormGroup.Control>
                </FormGroup>

                <FormGroup disabled={true}>
                  <FormGroup.Label text={intl.msg("credit_transaction_billing_address")} compact/>
                  <FormGroup.Control>
                    <div style={{whiteSpace: "pre-line"}}>
                      {creditTransaction?.billingInfo?.address}
                    </div>
                  </FormGroup.Control>
                </FormGroup>
              </>
          }

          {
            creditTransaction?.settledOn &&
              <FormGroup disabled={true}>
                <FormGroup.Label text={intl.msg("settled")} compact/>
                <FormGroup.Control>
                  <UserAndTimeLink user={creditTransaction?.settledBy} timestamp={creditTransaction?.settledOn} session={session} tenant={tenant}/>
                </FormGroup.Control>
              </FormGroup>
          }
        </FormGroups>
      )
    }

    if ("CREDIT_TRANSACTION" === creditTransaction?.refType && creditTransaction?.ref?.creditTransactionId) {
      ctls.push(
        <FormGroups>
          <FormGroup disabled={true}>
            <FormGroup.Label text={intl.msg(`credits_${"DEPOSIT" === creditTransaction?.creditTransactionType ? "deposit" : "original"}_transaction`)} compact/>
            <FormGroup.Controls>
              <a href={mkHref({prefix: env.consoleUrl, suffix: `/credits/${creditTransaction?.ref?.creditTransactionId}`, tenant: tenant, user: session?.user})} className="text-link" target="_blank">
                {creditTransaction?.ref?.creditTransactionId} - {creditTransaction?.ref?.description}
              </a>
            </FormGroup.Controls>
          </FormGroup>
        </FormGroups>
      )
    }

    if ("BROADCAST" === creditTransaction?.refType && creditTransaction?.ref?.broadcastId) {
      ctls.push(
        <FormGroups>
          <FormGroup disabled={true}>
            <FormGroup.Label text={intl.msg(`broadcast`)} compact/>
            <FormGroup.Controls>
              <a href={mkHref({prefix: env.consoleUrl, suffix: `/broadcasts/${creditTransaction?.ref?.broadcastId}`, tenant: tenant, user: session?.user})} className="text-link" target="_blank">
                {creditTransaction?.ref?.broadcastName}
              </a>
            </FormGroup.Controls>
          </FormGroup>
        </FormGroups>
      )
    }

    if ("REVERSED" === creditTransaction?.status && creditTransaction?.reversedRef) {
      ctls.push(
        <FormGroups>
          <FormGroup disabled={true}>
            <FormGroup.Label text={intl.msg("credits_reversal_transaction")} compact/>
            <FormGroup.Controls>
              <a href={mkHref({prefix: env.consoleUrl, suffix: `/credits/${creditTransaction?.reversedRef?.creditTransactionId}`, tenant: tenant, user: session?.user})} className="text-link" target="_blank">
                {creditTransaction?.reversedRef?.creditTransactionId} - {creditTransaction?.reversedRef?.description}
              </a>
            </FormGroup.Controls>
          </FormGroup>
        </FormGroups>
      )
    }

    if (ctls.length > 0) {
      return (
        ctls
      )
    }
    else {
      return (<></>)
    }

  }

  return (
    tenant &&
    <>
      <MainContainer tenant={tenant} menu={MainMenu}>

        <FormHeader>
          <AccountTabs tenant={tenant}/>
          <FormHeader.Toolbar>
            <FormHeader.Toolbar.Title>
              <div>{intl.msg("credit_transaction")}</div>
              <CreditTransactionStatus row={creditTransaction} size="lg"/>
            </FormHeader.Toolbar.Title>
            <FormHeader.Toolbar.Controls>
              <FormControls/>
            </FormHeader.Toolbar.Controls>
          </FormHeader.Toolbar>
          <FormHeader.Alert alert={alert}/>
        </FormHeader>
        <FormBody>

          <FormGroups>

            <FormGroup hidden={!tenant || !creditTransaction || !tenant.accessingAs("*/INSTREAM/SYSTEM,RESELLER")}>
              <FormGroup.Label text={intl.msg("account_name")} compact/>
              <FormGroup.Control>
                <TenantLink tenant={creditTransaction?.tenant} user={session.user} withTags/>
              </FormGroup.Control>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg("credits_transaction_id")} compact/>
              <FormGroup.Control>
                {creditTransaction?.creditTransactionId || "..."}
              </FormGroup.Control>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg("credits_transaction_type")} compact/>
              <FormGroup.Control>
                {creditTransaction?.creditTransactionType ? intl.msg(`credit_transaction_type_${creditTransaction?.creditTransactionType.toLowerCase()}`) : "..."}
              </FormGroup.Control>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg("description")} compact/>
              <FormGroup.Control>
                {creditTransaction?.description || "..."}
              </FormGroup.Control>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg("credits_amount")} compact/>
              <FormGroup.Control>
                {creditTransaction?.credits ? intl.num(creditTransaction?.credits, "SIGNED") : "..."} <span className="text-muted">{"PENDING" === creditTransaction?.status ? "(pending)" : ""}</span>
              </FormGroup.Control>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg("created")} compact/>
              <FormGroup.Control>
                <UserAndTimeLink user={creditTransaction?.createdBy} timestamp={creditTransaction?.createdOn} session={session} tenant={tenant}/>
              </FormGroup.Control>
            </FormGroup>

          </FormGroups>

          <RelationInfo/>

        </FormBody>
      </MainContainer>

      <CreditTransactionReverseDialog
        tenant={tenant}
        creditTransaction={creditTransaction}
      />

      <CreditTransactionBill
        ref={invoiceRef}
        tenant={tenant}
        creditTransaction={creditTransaction}
        billingInfo={creditTransaction?.billingInfo}
        bankAccounts={bankAccounts}
      />

      <CreditTransactionSettleDialog
        tenant={tenant}
        creditTransaction={creditTransaction}
      />

      <CreditTransactionDeleteDialog
        tenant={tenant}
        creditTransaction={creditTransaction}
      />

    </>

  )
}
